import type { JsonValue } from "@types";
import type { CaptureResult } from "posthog-js";
import { redactFirebaseActionUrl } from "@/utils/sentryAuthRedaction";
import {
  hasObjectType,
  isBooleanValue,
  isNumberValue,
  isStringValue,
} from "@/utils/typePredicates";

/**
 * PII scrubbing for PostHog's `before_send` hook.
 *
 * The curated `capture()` events and route URLs are PII-clean, but two
 * *automatic* channels can carry customer PII on enabled companies:
 *  - Autocapture (`$autocapture`) records the text content of clicked elements
 *    (`$elements` / `elements_chain`). Input *values* are masked by the SDK
 *    default, but rendered text — a customer name, phone, transcript snippet on
 *    Conversations/Coach/Reports — is not.
 *  - `$current_url` captures the full path + query (clean today, but not
 *    guaranteed forever).
 *
 * This redacts email, phone, street-address, and Firebase auth-credential
 * (`oobCode`/`apiKey` query params — see SD-12435) patterns from every event's
 * `properties` recursively, so it covers both channels plus any future event that
 * accidentally includes PII. It deliberately does NOT touch `$set` / `$set_once`
 * — those carry the intentional `identify()` email/name person properties —
 * including when posthog-js nests them INSIDE `properties` (repeat-identify
 * `$set` events do this; redacting there overwrote person emails with the
 * placeholder). Identity-plumbing fields on `$identify` / `$create_alias`
 * (`alias`, `distinct_id`, `$anon_distinct_id`) are likewise preserved: the
 * email-as-alias IS the HubSpot person linkage (SD-10017), and redacting it
 * collapsed every user's alias onto one shared placeholder string.
 */

// NOTE: the `/g` regexes below are module-level and therefore stateful
// (`lastIndex`). They are ONLY safe because `String.prototype.replace` resets
// `lastIndex` on every call — do NOT call `.test()` / `.exec()` on them, or a
// subsequent `.replace()` would silently start matching mid-string.

// Word-boundary anchored so we don't redact inside longer alphanumeric runs
// (e.g. a 24-hex Mongo `_id` in `?customer=`), per the word-boundary rule.
const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
// Percent-encoded email in a URL query string (`jane%40example.com`), where the
// `@` is escaped as `%40` and so slips past EMAIL_RE. `i` flag: hex is
// case-insensitive (`%40`/`%4a`); the local-part class already allows `%`.
const EMAIL_ENCODED_RE =
  /\b[A-Za-z0-9._%+-]+%40[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/gi;
// North-American 10-digit numbers with optional +1 and common separators
// (`(555) 123-4567`, `555.123.4567`, `5551234567`). The `(?!\w)` tail rejects
// longer digit runs (epoch ms, IDs) so only phone-shaped values match.
const PHONE_NA_RE =
  /(?<!\w)(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}(?!\w)/g;
// E.164 international (`+442071234567`) not covered by the NA shape above.
const PHONE_INTL_RE = /(?<!\w)\+\d{7,15}(?!\w)/g;
// Street-address line: house number + up to 4 name words + a street-type
// suffix (`123 Main St`, `1600 Pennsylvania Avenue`, `742 Evergreen Terrace`).
// Anchored on the suffix — addresses have no clean pattern, so this matches the
// street line only (not city/state/ZIP; a bare 5-digit ZIP is indistinguishable
// from an ID/count and is intentionally NOT matched). Case-insensitive, so it
// will occasionally over-redact prose like "5 minute Drive" — acceptable for PII.
const STREET_SUFFIXES =
  "St|Street|Ave|Avenue|Blvd|Boulevard|Rd|Road|Dr|Drive|Ln|Lane|Ct|Court|Way|Pl|Place|Ter|Terrace|Cir|Circle|Hwy|Highway|Pkwy|Parkway|Trl|Trail|Sq|Square|Loop|Pike|Row|Run|Aly|Alley";
const ADDRESS_RE = new RegExp(
  `\\b\\d{1,6}\\s+(?:[A-Za-z0-9.'-]+\\s+){0,4}(?:${STREET_SUFFIXES})\\b`,
  "gi",
);

const EMAIL_PLACEHOLDER = "[redacted-email]";
const PHONE_PLACEHOLDER = "[redacted-phone]";
const ADDRESS_PLACEHOLDER = "[redacted-address]";
const AUTH_PLACEHOLDER = "[redacted-auth]";
const POSTHOG_FIREBASE_AUTH_PARAMETERS: ReadonlySet<string> = new Set([
  "oobcode",
  "apikey",
]);
const POSTHOG_FIREBASE_REDACTION_OPTIONS = {
  sensitiveParameterNames: POSTHOG_FIREBASE_AUTH_PARAMETERS,
  redactedValue: AUTH_PLACEHOLDER,
};

/**
 * Replace Firebase auth-credential + email + phone + address substrings in a
 * string with placeholders.
 */
function redactPII(text: string): string {
  // Firebase auth params first, including encoded/nested action URLs. Then email
  // (its digits can't then be re-matched as a phone), then phones (so a phone's
  // digits aren't consumed by the address house-number match), then addresses.
  return redactFirebaseActionUrl(text, POSTHOG_FIREBASE_REDACTION_OPTIONS)
    .replace(EMAIL_RE, EMAIL_PLACEHOLDER)
    .replace(EMAIL_ENCODED_RE, EMAIL_PLACEHOLDER)
    .replace(PHONE_NA_RE, PHONE_PLACEHOLDER)
    .replace(PHONE_INTL_RE, PHONE_PLACEHOLDER)
    .replace(ADDRESS_RE, ADDRESS_PLACEHOLDER);
}

/**
 * Recursively scrub strings within any property value (objects, arrays).
 *
 * `seen` tracks the current descent path (added on enter, removed on exit) so a
 * self-referential object short-circuits instead of recursing forever, while a
 * shared (non-cyclic) node is still scrubbed each time it appears. No arbitrary
 * depth cap — every nested string is visited regardless of shape.
 */
function scrubValue(
  value: JsonValue | undefined,
  seen: WeakSet<object>,
): JsonValue | undefined {
  if (isStringValue(value)) {
    return redactPII(value);
  }
  if (
    value === null ||
    isBooleanValue(value) ||
    isNumberValue(value) ||
    value === undefined
  ) {
    return value;
  }
  if (!hasObjectType(value)) return null;
  if (seen.has(value)) {
    return null;
  }
  seen.add(value);
  const result: JsonValue = Array.isArray(value)
    ? value.map((item) => scrubValue(item, seen) ?? null)
    : Object.fromEntries(
        Object.entries(value).map(([key, item]) => [
          key,
          scrubValue(item, seen),
        ]),
      );
  seen.delete(value); // pop off the current path
  return result;
}

type EventProperties = NonNullable<CaptureResult["properties"]>;

// Person properties set via identify()/setPersonProperties ride inside
// `properties` on `$set` events — intentional PII, never scrubbed.
const PERSON_PROPERTY_KEYS = new Set(["$set", "$set_once"]);

// Identity events whose linkage fields are the point of the event: redacting
// `alias` turns every user's email alias into the same placeholder string.
const IDENTITY_EVENTS = new Set(["$identify", "$create_alias"]);
const IDENTITY_PROPERTY_KEYS = new Set([
  "alias",
  "distinct_id",
  "$anon_distinct_id",
]);

function scrubProperties(
  properties: EventProperties,
  preservedKeys: ReadonlySet<string>,
): EventProperties {
  const seen = new WeakSet<object>();
  const scrubbed: EventProperties = {};
  for (const [key, value] of Object.entries(properties)) {
    scrubbed[key] =
      PERSON_PROPERTY_KEYS.has(key) || preservedKeys.has(key)
        ? value
        : scrubValue(value, seen);
  }
  return scrubbed;
}

const NO_EXTRA_KEYS: ReadonlySet<string> = new Set();

/**
 * `before_send` callback: return a copy of the event with PII redacted from its
 * `properties`. `$set` / `$set_once` (identify person properties) pass through
 * untouched wherever they appear, and identity events keep their linkage
 * fields (`alias`, `distinct_id`, `$anon_distinct_id`). Null events (already
 * dropped elsewhere) pass through unchanged.
 */
export function sanitizeAnalyticsEvent(
  event: CaptureResult | null,
): CaptureResult | null {
  if (!event?.properties) {
    return event;
  }
  const preservedKeys = IDENTITY_EVENTS.has(event.event)
    ? IDENTITY_PROPERTY_KEYS
    : NO_EXTRA_KEYS;
  return {
    ...event,
    properties: scrubProperties(event.properties, preservedKeys),
  };
}
